import { Modal } from 'react-bootstrap';
import { Typography } from 'Components/atoms';
import { AiOutlineClose } from '@react-icons/all-files/ai/AiOutlineClose';
import { getDetailedEngagementLog } from 'Apis/library';
import { useEffect, useState } from 'react';
import {
    EngagementLogEventDto,
    FlowStateWithRelationsDto,
    _EngagementLogEventType,
    _EngagementLogEventActor,
} from '@ampsec/platform-client';
import { LogEventDateTimeFormatter } from 'Core-utils/helpers/helpers';
import {
    getEngagementLogEventTimestamp,
    getActionExecutionDetailPath,
    getActionExecutionEventData,
    getActionExecutionOutcomeLabel,
    getFlowStateCreatedLabel,
    getFlowStateCreatedTimestamp,
    getStateTransactionLabel,
    shouldRenderStateTransactionEvent,
} from './engagementLogHistoryDisplay';
import './styles.scss';

interface EngagementLogDetailModalProps {
    onClose: () => void;
    show: boolean;
    flowState: FlowStateWithRelationsDto | null;
}

const EVENT_ACTOR = _EngagementLogEventActor.enum;
const EVENT_TYPE = _EngagementLogEventType.enum;

const renderEventWithType = (event: EngagementLogEventDto, index: number): React.ReactNode => {
    switch (event.eventType) {
        case EVENT_TYPE.Dialogue:
            return renderDialogue(event, index);
        case EVENT_TYPE.ConversationContext:
            return renderConversationContext(event);
        case EVENT_TYPE.Notification:
            return renderNotification(event);
        case EVENT_TYPE.StateTrigger:
        case EVENT_TYPE.StateTransaction:
        case EVENT_TYPE.BackgroundStateTransaction:
            return renderTransaction(event, index);
        case EVENT_TYPE.ActionExecution:
            return renderActionExecution(event);
        default:
            return null;
    }
};

const renderConversationContext = (event: EngagementLogEventDto): React.ReactNode => {
    return (
        <div key={`conversation-context-${event.data?.id}`} className={`dialogue-item dialogue-item__ampy`}>
            <div className="message-content">
                <p>{event.data?.conversationContext?.context}</p>
            </div>
            <span className="delivery-details">
                {event.actor === EVENT_ACTOR.BOT ? 'Ampy' : 'End User'} @{' '}
                {LogEventDateTimeFormatter(getEngagementLogEventTimestamp(event))}
            </span>
        </div>
    );
};

const renderDialogue = (event: EngagementLogEventDto, index: number): React.ReactNode => {
    const dialogueClass = event.actor === EVENT_ACTOR.USER ? 'dialogue-item__user' : 'dialogue-item__ampy';
    const key = event.data?.id ?? `dialogue-${index}`;
    const content = event.data?.content ?? '';
    const isButtonClick = content.includes('User Clicked Button:');
    const dialogueContentClass = isButtonClick ? 'action-button' : '';
    const colonIndex = content.indexOf(':');
    const dialogeContent = isButtonClick ? (
        <p>
            {content.slice(0, colonIndex).trimEnd()}{' '}
            <span className={dialogueContentClass}>{content.slice(colonIndex + 1).trimStart()}</span>
        </p>
    ) : (
        <p>{content}</p>
    );
    return (
        <div key={key} className={`dialogue-item ${dialogueClass}`}>
            <div className="message-content">{dialogeContent}</div>
            <span className="delivery-details">
                {event.actor === EVENT_ACTOR.USER ? 'End User' : 'Ampy'} @{' '}
                {LogEventDateTimeFormatter(getEngagementLogEventTimestamp(event))}
            </span>
        </div>
    );
};

const renderNotification = (event: EngagementLogEventDto): React.ReactNode => {
    return (
        <div key={`notification-${event.data?.id}`} className="dialogue-item dialogue-item__transaction">
            <div className="message-content">
                <p>
                    {event.data?.context} sent to {event.data?.deliveryChannel}
                    <span className="delivery-details">
                        {event.actor} @ {LogEventDateTimeFormatter(getEngagementLogEventTimestamp(event))}
                    </span>
                </p>
            </div>
        </div>
    );
};

const renderActionExecution = (event: EngagementLogEventDto): React.ReactNode => {
    const actionExecution = getActionExecutionEventData(event);
    const outcomeLabel = getActionExecutionOutcomeLabel(actionExecution.status);

    return (
        <div key={`action-execution-${actionExecution.id}`} className="dialogue-item dialogue-item__transaction">
            <div className="message-content">
                <p>
                    {outcomeLabel}
                    <a
                        href={getActionExecutionDetailPath(actionExecution.actionSpecId)}
                        target="_blank"
                        rel="noreferrer"
                    >
                        {actionExecution.actionName}
                    </a>
                    <span className="delivery-details">
                        {event.actor} @ {LogEventDateTimeFormatter(getEngagementLogEventTimestamp(event))}
                    </span>
                </p>
            </div>
        </div>
    );
};

const renderStateTransaction = (event: EngagementLogEventDto, index: number): React.ReactNode => {
    const content = getStateTransactionLabel(event);
    return (
        <div key={`transaction-${index}`} className="dialogue-item dialogue-item__transaction">
            <div className="message-content">
                <p>
                    {content}
                    <span className="delivery-details">
                        {event.actor} @ {LogEventDateTimeFormatter(getEngagementLogEventTimestamp(event))}
                    </span>
                </p>
            </div>
        </div>
    );
};

const renderTransaction = (event: EngagementLogEventDto, index: number): React.ReactNode => {
    if (!shouldRenderStateTransactionEvent(event)) {
        return null;
    }
    return renderStateTransaction(event, index);
};

const renderFlowStateInfo = (flowState: FlowStateWithRelationsDto): React.ReactNode => {
    return (
        <div key={`transaction-flow-state-created`} className="dialogue-item dialogue-item__transaction">
            <div className="message-content">
                <p>
                    {getFlowStateCreatedLabel(flowState)}
                    <span className="delivery-details">
                        {EVENT_ACTOR.SYSTEM} @ {LogEventDateTimeFormatter(getFlowStateCreatedTimestamp(flowState))}
                    </span>
                </p>
            </div>
        </div>
    );
};

const renderEvents = (events: EngagementLogEventDto[]): React.ReactNode => {
    return events.map((event, index) => renderEventWithType(event, index));
};

const getModalHeaderContent = (flowState?: FlowStateWithRelationsDto): React.ReactNode => {
    if (!flowState || !flowState.user || !flowState.finding) {
        return 'Engagement Log';
    }
    return `Conversation with ${flowState.user?.firstName} ${flowState.user?.lastName} about a ${flowState.finding?.category} finding`;
};

const EngagementLogDetailModal = ({ show, onClose, flowState }: EngagementLogDetailModalProps) => {
    const [detailedEngagementLog, setDetailedEngagementLog] = useState<FlowStateWithRelationsDto | null>(null);

    useEffect(() => {
        if (!flowState || !flowState.specId || !flowState.id) {
            return;
        }
        getDetailedEngagementLog(flowState.id).then((res: any) => {
            setDetailedEngagementLog(res.data[0]);
        });
    }, [flowState]);

    const feed = detailedEngagementLog?.log ?? [];
    const flowStateForHeader = detailedEngagementLog ?? flowState;

    return (
        <Modal show={show} className="notification-detail-modal" onHide={onClose}>
            <Modal.Header>
                <div className="modal-header">
                    <Typography variant="subtitle1" color="text-high-emphasis">
                        {getModalHeaderContent(flowStateForHeader!)}
                    </Typography>
                </div>
                <AiOutlineClose
                    className="text-text-medium-emphasis notification-detail-modal__icon"
                    onClick={onClose}
                />
            </Modal.Header>
            <Modal.Body className="custom-scrollbar overflow-auto">
                <div className="notification-detail-modal__content">
                    <div className="modal-content__dialogues">
                        {flowStateForHeader && renderFlowStateInfo(flowStateForHeader)}
                        {feed.length > 0 ? renderEvents(feed) : 'No events available'}
                    </div>
                </div>
            </Modal.Body>
        </Modal>
    );
};

export default EngagementLogDetailModal;
